import { CallApi } from '@/shared/api/methods/CallApi'
import { deviceSupportsSecretCalls, ensureE2EIdentityUploaded, CAPABILITY_SECRET_CALLS } from '@/shared/crypto/bootstrap'
import { fetchPeerKeyFresh, getKnownPeerKey, rememberPeerKey } from '@/shared/crypto/peerKeys'
import { getPublicKeyBase64 } from '@/shared/crypto/keyStore'
import {
  CallKeyBinding,
  CallKeyEnvelope,
  envelopeBelongsToThisDevice,
  generateCallKey,
  isCallKeyEnvelope,
  openCallKey,
  sealCallKey,
} from '@/shared/crypto/callKey'
import { confirmSecretCall, showSecretCallNotice } from './secretCallNotice'
import { SECRET_CALL_TEXTS } from './secretCallTexts'

/**
 * Секретный звонок 1-на-1 на устройстве (docs/encryption/E2E-CALLS.md).
 * Здесь всё, что нужно экранам: подготовить исходящий, разобрать отказ
 * сервера, проверить входящий и восстановить ключ после перезагрузки.
 */

export interface OutgoingSecretCall {
  envelope: CallKeyEnvelope
  key: string
  binding: CallKeyBinding
}

/**
 * Подготовить исходящий секретный звонок: проверить браузер и собеседника,
 * создать ключ звонка и конверт. null — звонок не начинается, причина уже
 * показана пользователю.
 */
export async function prepareOutgoingSecretCall(
  chatId: number,
  myUserId: number,
  peerUserId: number,
): Promise<OutgoingSecretCall | null> {
  if (!deviceSupportsSecretCalls()) {
    await showSecretCallNotice(SECRET_CALL_TEXTS.browser)
    return null
  }

  await ensureE2EIdentityUploaded()

  const fresh = await fetchPeerKeyFresh(peerUserId)
  if (!fresh || !fresh.capabilities.includes(CAPABILITY_SECRET_CALLS)) {
    await showSecretCallNotice(SECRET_CALL_TEXTS.peerUnsupported)
    return null
  }

  if (fresh.changed) {
    const go = await confirmSecretCall(SECRET_CALL_TEXTS.keyChanged, 'Позвонить')
    if (!go) return null
    await rememberPeerKey(peerUserId, fresh.publicKey)
  }

  const binding: CallKeyBinding = { chatId, callerId: myUserId, calleeId: peerUserId }
  const key = generateCallKey()
  const envelope = await sealCallKey(key, binding, fresh.publicKey)

  return { envelope, key, binding }
}

/** Что делать после отказа сервера в секретном звонке. */
export type SecretCallErrorAction = 'retry' | 'handled' | 'unhandled'

/** Код отказа секретного звонка из ответа сервера, если это он. */
export function secretCallErrorCode(e: unknown): string | null {
  const code = (e as any)?.response?.data?.code
  return typeof code === 'string' && code.startsWith('SECRET_CALL_') ? code : null
}

/**
 * Разобрать отказ сервера: показать понятное сообщение и сказать, стоит ли
 * повторить звонок. Не секретный отказ — 'unhandled', его обрабатывает экран.
 */
export async function handleSecretCallError(e: unknown): Promise<SecretCallErrorAction> {
  switch (secretCallErrorCode(e)) {
    case null:
      return 'unhandled'
    case 'SECRET_CALL_PEER_UNSUPPORTED':
      await showSecretCallNotice(SECRET_CALL_TEXTS.peerUnsupported)
      return 'handled'
    case 'SECRET_CALL_PEER_KEY_CHANGED': {
      // Кэш ключа устарел: следующий заход перечитает ключ и предупредит о смене.
      const again = await confirmSecretCall(SECRET_CALL_TEXTS.keyChanged, 'Позвонить ещё раз')
      return again ? 'retry' : 'handled'
    }
    case 'SECRET_CALL_CALLER_KEY_STALE':
      // Ключ этого устройства ещё не на сервере — публикуем и повторяем.
      await ensureE2EIdentityUploaded(true)
      return 'retry'
    case 'SECRET_CALL_ENVELOPE_REQUIRED':
    case 'SECRET_CALL_DEVICE_UNSUPPORTED':
      await showSecretCallNotice(SECRET_CALL_TEXTS.appOutdated)
      return 'handled'
    case 'SECRET_CALL_OTHER_DEVICE':
      await showSecretCallNotice(SECRET_CALL_TEXTS.otherDevice)
      return 'handled'
    default:
      await showSecretCallNotice(SECRET_CALL_TEXTS.keyFailed)
      return 'handled'
  }
}

export type IncomingSecretCallCheck =
  | { status: 'ok'; key: string; envelope: CallKeyEnvelope; keyChanged: boolean; devicePublicKey: string }
  | { status: 'other_device' }
  | { status: 'failed' }

/**
 * Проверить входящий секретный звонок до кнопки «Принять» (§3.6): конверт
 * адресован этому устройству, открывается, привязан к этому звонку.
 * Конверта нет в уведомлении (пришли из истории) — берём из calls/active.
 */
export async function checkIncomingSecretCall(
  callId: number,
  chatId: number,
  callerId: number,
  myUserId: number,
  envelope: unknown,
): Promise<IncomingSecretCallCheck> {
  let env = envelope
  if (!isCallKeyEnvelope(env)) {
    env = await envelopeFromActiveCall(callId)
  }
  if (!isCallKeyEnvelope(env)) return { status: 'failed' }

  if (!(await envelopeBelongsToThisDevice(env))) return { status: 'other_device' }

  const opened = await openCallKey(env, { chatId, callerId, calleeId: myUserId })
  if (!opened.ok) {
    return opened.reason === 'other_device' ? { status: 'other_device' } : { status: 'failed' }
  }

  // Ключ звонящего сравниваем с тем, которому доверяли раньше (§6).
  const known = await getKnownPeerKey(callerId)
  const keyChanged = known !== null && known !== opened.peerPublicKey
  if (known === null) {
    await rememberPeerKey(callerId, opened.peerPublicKey)
  }

  return {
    status: 'ok',
    key: opened.key,
    envelope: env,
    keyChanged,
    devicePublicKey: await getPublicKeyBase64(),
  }
}

/** Запомнить новый ключ собеседника после того, как пользователь принял звонок. */
export async function trustPeerKeyFromEnvelope(peerUserId: number, envelope: CallKeyEnvelope): Promise<void> {
  const mine = await getPublicKeyBase64()
  await rememberPeerKey(peerUserId, envelope.rpk === mine ? envelope.spk : envelope.rpk)
}

/**
 * Восстановить ключ идущего секретного звонка после перезагрузки страницы:
 * ключ нигде не хранится, конверт открывается заново (§3.3).
 */
export async function restoreSecretCallKey(callId: number): Promise<string | null> {
  let res: any
  try {
    res = await CallApi.active()
  } catch {
    return null
  }
  if (!res || Number(res.call_id) !== Number(callId) || !res.is_secret) return null

  const opened = await openCallKey(res.key_envelope, {
    chatId: Number(res.chat_id),
    callerId: Number(res.caller?.id),
    calleeId: Number(res.callee?.id),
  })

  return opened.ok ? opened.key : null
}

/**
 * Ключ звонка для подключения к комнате. Обычный звонок — без ключа; секретный
 * — из памяти, а после перезагрузки страницы заново из конверта.
 */
export async function secretKeyForCall(
  call: { callId: number; isSecret?: boolean; e2eKey?: string },
): Promise<{ ok: true; key: string | null } | { ok: false }> {
  if (!call.isSecret) return { ok: true, key: null }
  if (call.e2eKey) return { ok: true, key: call.e2eKey }

  const key = await restoreSecretCallKey(+call.callId)
  if (!key) return { ok: false }
  call.e2eKey = key
  return { ok: true, key }
}

/** Ключ собеседника, которым запечатан конверт этого звонка. */
export async function peerKeyOfEnvelope(envelope: unknown): Promise<string> {
  if (!isCallKeyEnvelope(envelope)) return ''
  const mine = await getPublicKeyBase64()
  return envelope.rpk === mine ? envelope.spk : envelope.rpk
}

async function envelopeFromActiveCall(callId: number): Promise<unknown> {
  try {
    const res: any = await CallApi.active()
    return res && Number(res.call_id) === Number(callId) ? res.key_envelope : null
  } catch {
    return null
  }
}
